import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Home, ArrowRight, Menu, X, Scissors, Maximize, FileText, Crop } from 'lucide-react';
import { cn } from '../lib/utils';

const navLinks = [
  { name: 'Home', path: '/', icon: Home },
  { name: 'Meesho', path: '/meesho-cropper', icon: Scissors },
  { name: 'Flipkart', path: '/flipkart-cropper', icon: Maximize },
  { name: 'Amazon', path: '/amazon-cropper', icon: FileText },
];

export default function Navbar() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const navLinksList = [
    { name: 'Home', path: '/', icon: Home },
    { name: 'Meesho Cropper', path: '/meesho-cropper', icon: Scissors },
    { name: 'Flipkart Cropper', path: '/flipkart-cropper', icon: Maximize },
    { name: 'Amazon Cropper', path: '/amazon-cropper', icon: FileText },
    { name: 'PDF Cropper', path: '/manual-cropper', icon: Crop },
  ];

  const tools = [
    {
      name: 'Meesho Smart Cropper',
      path: '/meesho-cropper',
      logo: 'https://webtoolsdeck.com/wp-content/uploads/2026/04/Meesho_logo.png',
      color: 'bg-[#FFF0F7]',
      borderColor: 'border-[#FAD2E5]',
    },
    {
      name: 'Flipkart 4x6 Resizer',
      path: '/flipkart-cropper',
      logo: 'https://webtoolsdeck.com/wp-content/uploads/2026/04/Flipkart-logo-1-512x143-1.png',
      color: 'bg-[#F0F5FF]',
      borderColor: 'border-[#DCE9FF]',
    },
    {
      name: 'Amazon Label Studio',
      path: '/amazon-cropper',
      logo: 'https://webtoolsdeck.com/wp-content/uploads/2026/04/pngimg.com-amazon_PNG12.png',
      color: 'bg-[#FFF8E7]',
      borderColor: 'border-[#FFE8B3]',
    },
    {
      name: 'PDF Cropper Studio',
      path: '/manual-cropper',
      logo: 'https://webtoolsdeck.com/wp-content/uploads/2025/12/Untitled-design-2-scaled-e1765227637238.png',
      color: 'bg-[#F0FFF4]',
      borderColor: 'border-[#C6F6D5]',
    }
  ];

  return (
    <nav className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-100">
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        <Link 
          to="/" 
          className="flex items-center gap-3"
          onClick={() => setIsMenuOpen(false)}
        >
          <img 
            src="https://webtoolsdeck.com/wp-content/uploads/2025/12/Untitled-design-2-scaled-e1765227637238.png" 
            alt="WebTools Deck Logo" 
            className="h-10 md:h-12 w-auto object-contain"
            width="160"
            height="48"
            referrerPolicy="no-referrer"
            fetchPriority="high"
          />
        </Link>

        {/* Desktop Links */}
        <div className="hidden lg:flex items-center gap-1">
          {navLinksList.map((link) => {
            const Icon = link.icon;
            const isActive = location.pathname === link.path;
            
            return (
              <Link
                key={link.path}
                to={link.path}
                className={cn(
                  "px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2",
                  isActive ? "text-indigo-700 bg-indigo-50" : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                )}
              >
                <Icon className="w-4 h-4" />
                {link.name}
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-3">
          <button 
            onClick={() => setIsModalOpen(true)}
            aria-label="Open Studio Selector"
            className="hidden sm:flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100"
          >
            Launch Studio
            <ArrowRight className="w-4 h-4" />
          </button>
          
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-gray-900 focus:outline-none bg-gray-50 rounded-lg"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Tool Selection Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-lg bg-white rounded-[40px] shadow-2xl overflow-hidden p-8 md:p-10 space-y-8"
            >
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h3 className="text-2xl font-black text-gray-900 tracking-tight">Select Studio</h3>
                  <p className="text-sm text-gray-500 font-bold">Choose a marketplace to start cropping.</p>
                </div>
                <button 
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="Close studio selector"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="grid gap-4">
                {tools.map((tool) => (
                  <Link
                    key={tool.path}
                    to={tool.path}
                    onClick={() => setIsModalOpen(false)}
                    className={cn(
                      "group flex items-center gap-5 p-5 rounded-3xl border-2 transition-all hover:shadow-xl hover:-translate-y-1",
                      tool.color,
                      tool.borderColor
                    )}
                  >
                    <div className="w-16 h-12 bg-white rounded-xl flex items-center justify-center p-2 shadow-sm shrink-0">
                      <img 
                        src={tool.logo} 
                        alt={tool.name} 
                        width="48"
                        height="32"
                        className="max-w-full max-h-full object-contain"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-black text-gray-900 tracking-tight">{tool.name}</h4>
                      <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-0.5">Professional Studio</p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-gray-400 group-hover:text-gray-900 group-hover:bg-opacity-100 transition-all shadow-sm">
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-20 inset-x-0 bg-white border-b border-gray-200 shadow-2xl lg:hidden overflow-hidden"
          >
            <div className="p-6 space-y-3">
              {navLinksList.map((link) => {
                const Icon = link.icon;
                const isActive = location.pathname === link.path;
                
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={cn(
                      "flex items-center gap-4 p-4 rounded-2xl text-lg font-black transition-all",
                      isActive ? "bg-indigo-600 text-white shadow-xl shadow-indigo-200" : "text-gray-900 hover:bg-gray-100"
                    )}
                  >
                    <div className={cn(
                      "w-10 h-10 rounded-xl flex items-center justify-center",
                      isActive ? "bg-white/20" : "bg-gray-100"
                    )}>
                      <Icon className="w-6 h-6" />
                    </div>
                    {link.name}
                  </Link>
                );
              })}
              
              <div className="pt-6 mt-6 border-t border-gray-100">
                <button 
                  onClick={() => {
                    setIsMenuOpen(false);
                    setIsModalOpen(true);
                  }}
                  className="flex items-center justify-center gap-3 bg-indigo-600 text-white w-full py-5 rounded-2xl font-black text-lg shadow-2xl shadow-indigo-100"
                >
                  Launch Studio
                  <ArrowRight className="w-6 h-6" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
